'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import type { DashboardData } from '../page';
import { usePlatformSettings } from '@/contexts/PlatformSettingsContext';

interface Props {
  data: DashboardData;
}

const statusClass: Record<string, string> = {
  HEALTHY: 'status-healthy',
  WARNING: 'status-warning',
  CRITICAL: 'status-critical',
  IDLE: 'status-idle',
};

export default function CenterHealthColumn({ data }: Props) {
  const { settings: platformSettings } = usePlatformSettings();
  const { employees, payrollRecords, onboarding, scheduledShifts } = data;

  // HR Health: based on employee count and onboarding issues
  const rtwPending = employees.filter((e) => e.right_to_work === false).length;
  const hrStatus = employees.length === 0 ? 'IDLE' : rtwPending > 0 ? 'WARNING' : 'HEALTHY';
  const hrProgress = employees.length === 0 ? 0 : Math.max(0, Math.round(100 - (rtwPending / Math.max(employees.length, 1)) * 100));

  // Payroll Health
  const payrollHolds = payrollRecords.filter((p) => p.payroll_status === 'On Hold').length;
  const payrollStatus = payrollRecords.length === 0 ? 'IDLE' : payrollHolds > 0 ? 'WARNING' : 'HEALTHY';
  const payrollProgress = payrollRecords.length === 0 ? 0 : Math.max(0, Math.round(100 - (payrollHolds / Math.max(payrollRecords.length, 1)) * 100));

  // Shift Scheduling Health: compute coverage for today
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayMasters = scheduledShifts.filter((s) => s.shift_date === todayStr && !s.parent_shift_id);
  const todayRequired = todayMasters.reduce((sum, s) => {
    const positions: any[] = Array.isArray(s.positions_meta) ? s.positions_meta : [];
    const needed = positions.reduce((ps: number, p: any) => ps + (Number(p.headcount) || 1), 0);
    return sum + (needed > 0 ? needed : 1);
  }, 0);
  const todayFilled = scheduledShifts.filter(
    (s) => s.shift_date === todayStr && s.parent_shift_id && s.employee_id
  ).length;
  const coveragePct = todayRequired > 0 ? Math.round((todayFilled / todayRequired) * 100) : 0;
  const shiftAlerts = Math.max(0, todayRequired - todayFilled);
  const shiftStatus = scheduledShifts.length === 0
    ? 'IDLE'
    : todayRequired === 0
    ? 'IDLE'
    : coveragePct < 70
    ? 'CRITICAL'
    : coveragePct < 90
    ? 'WARNING' : 'HEALTHY';
  const shiftProgress = todayRequired > 0 ? coveragePct : 0;
  const shiftColor = shiftStatus === 'HEALTHY' ? 'var(--color-green)'
    : shiftStatus === 'WARNING' ? 'var(--color-amber)'
    : shiftStatus === 'CRITICAL' ? 'var(--color-red)' : 'var(--muted-foreground)';

  const allCenters = [
    {
      id: 'center-hr',
      name: 'HR Center',
      icon: 'UserGroupIcon',
      status: hrStatus,
      alerts: rtwPending,
      progress: hrProgress,
      color: hrStatus === 'HEALTHY' ? 'var(--color-green)' : hrStatus === 'WARNING' ? 'var(--color-amber)' : 'var(--muted-foreground)',
      enabled: platformSettings.centreHrEnabled,
    },
    {
      id: 'center-payroll',
      name: 'Payroll Center',
      icon: 'BanknotesIcon',
      status: payrollStatus,
      alerts: payrollHolds,
      progress: payrollProgress,
      color: payrollStatus === 'HEALTHY' ? 'var(--color-green)' : payrollStatus === 'WARNING' ? 'var(--color-amber)' : 'var(--muted-foreground)',
      enabled: platformSettings.centrePayrollEnabled,
    },
    {
      id: 'center-shift',
      name: 'Shift Scheduling',
      icon: 'CalendarDaysIcon',
      status: shiftStatus,
      alerts: shiftAlerts,
      progress: shiftProgress,
      color: shiftColor,
      enabled: platformSettings.centreShiftSchedulingEnabled,
    },
    {
      id: 'center-transport',
      name: 'Employee Transport',
      icon: 'TruckIcon',
      status: 'IDLE',
      alerts: 0,
      progress: 0,
      color: 'var(--muted-foreground)',
      enabled: platformSettings.centreEmployeeTransportEnabled,
    },
    {
      id: 'center-warehouse',
      name: 'Warehouse Center',
      icon: 'BuildingStorefrontIcon',
      status: 'IDLE',
      alerts: 0,
      progress: 0,
      color: 'var(--muted-foreground)',
      enabled: platformSettings.centreWarehouseEnabled,
    },
    {
      id: 'center-clients',
      name: 'Clients & Sites',
      icon: 'BuildingOfficeIcon',
      status: 'IDLE',
      alerts: 0,
      progress: 0,
      color: 'var(--muted-foreground)',
      enabled: platformSettings.centreClientsSitesEnabled,
    },
  ];

  // Only show enabled centres
  const centers = allCenters.filter((c) => c.enabled);

  return (
    <div className="card-elevated rounded-xl p-4 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-card-foreground">Center Health Status</h3>
        <span className="w-2 h-2 rounded-full bg-color-green inline-block"></span>
      </div>
      {centers.length === 0 ? (
        <p className="text-xs text-muted-foreground italic py-4 text-center">No centres enabled</p>
      ) : (
        <div className="space-y-3">
          {centers.map((center) => (
            <div key={center.id} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name={center.icon as any} size={14} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-xs font-medium text-card-foreground truncate">{center.name}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {center.alerts > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded font-semibold"
                      style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--color-red)' }}>
                      {center.alerts}
                    </span>
                  )}
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusClass[center.status]}`}>
                    {center.status}
                  </span>
                </div>
              </div>
              {center.status !== 'IDLE' && (
                <div className="w-full h-1 rounded-full" style={{ background: 'var(--border)' }}>
                  <div
                    className="h-1 rounded-full transition-all duration-500"
                    style={{ width: `${center.progress}%`, background: center.color }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}